import { Link } from "react-router-dom";
import { IMAGES } from "@/data/content";
import { getLegal } from "@/data/legal";
import { useLang } from "@/i18n";
import BookButton from "@/components/BookButton";

const FOOTER_LINKS = [
  { to: "/", key: "home" },
  { to: "/services", key: "services" },
  { to: "/personal-support", key: "personal" },
  { to: "/about", key: "about" },
  { to: "/packages", key: "packages" },
  { to: "/faq", key: "faq" },
  { to: "/contact", key: "contact" },
];

export default function Footer() {
  const { t, c, lang } = useLang();
  const { docs: legalDocs, links: legalLinks } = getLegal(lang);
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/10 bg-obsidian">
      <img
        src={IMAGES.skyline}
        alt=""
        loading="lazy"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.08]"
      />
      <div className="relative mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <img src="/assets/vck-logo-dark.png" alt="VCK Business Lab" className="w-56 h-auto" />
            <p className="mt-5 font-serif text-xl italic text-slate-300">{t("footer.tagline")}</p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">{t("footer.blurb")}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <BookButton testid="footer-book-consultation-button" className="btn-gold !px-6 !py-3" />
              <Link to="/contact" data-testid="footer-request-quote-link" className="btn-outline-light !px-6 !py-3">
                {t("footer.quote")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="overline text-gold-muted">{t("footer.explore")}</p>
            <ul className="mt-5 space-y-3">
              {FOOTER_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.key}`}
                    className="text-sm text-slate-400 transition-colors duration-300 hover:text-gold"
                  >
                    {t(`nav.${l.key}`)}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/admin/login"
                  data-testid="footer-owner-login-link"
                  className="text-sm text-slate-500 transition-colors duration-300 hover:text-gold"
                >
                  {t("footer.ownerLogin")}
                </Link>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-4">
            <p className="overline text-gold-muted">{t("footer.services")}</p>
            <ul className="mt-5 space-y-3">
              {c.services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/services"
                    data-testid={`footer-service-${s.slug}`}
                    className="text-sm text-slate-400 transition-colors duration-300 hover:text-gold"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-16 border-t border-white/10 pt-8">
          <nav
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2"
            aria-label="Legal"
            data-testid="footer-legal-links"
          >
            {legalLinks.map((l) => (
              <Link
                key={l.key}
                to={legalDocs[l.key].path}
                data-testid={`footer-legal-${l.key}`}
                className="text-xs text-slate-500 transition-colors duration-300 hover:text-gold"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="mt-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} {t("footer.rights")}
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{t("footer.virtual")}</p>
          </div>
          <p className="mt-4 text-center text-xs leading-relaxed text-slate-400" data-testid="footer-legal-entity">
            VCK Business Lab · office@vckbusinesslab.com · +1 587 385 6058 · Edmonton, Alberta, Canada · Serving clients worldwide
          </p>
        </div>
      </div>
    </footer>
  );
}
